import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAclModuleDetail, useUpdateAclModule } from '../api';
import type { AclModuleChild } from '../types';

export function UpdateAclModulePage() {
  const navigate = useNavigate();
  const { mid } = useParams<{ mid: string }>();
  const moduleId = Number(mid);

  const detail = useAclModuleDetail(moduleId);
  const update = useUpdateAclModule();

  const [parentName, setParentName] = useState('');
  const [children, setChildren] = useState<string[]>(['']);

  // Seed local state once the detail resolves. Subsequent edits stay local.
  useEffect(() => {
    if (!detail.data) return;
    setParentName(detail.data.mname ?? '');
    const raw = detail.data.childModule;
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as AclModuleChild[];
        const names = Array.isArray(parsed)
          ? parsed.map((c) => c?.moduleName ?? '').filter(Boolean)
          : [];
        setChildren(names.length ? names : ['']);
      } catch {
        setChildren(['']);
      }
    } else {
      setChildren(['']);
    }
  }, [detail.data]);

  const addChild = () => {
    const last = children[children.length - 1] ?? '';
    if (!last.trim()) {
      toast.error('Fill the current child module before adding another.');
      return;
    }
    setChildren([...children, '']);
  };

  const removeChild = (idx: number) => {
    const next = children.filter((_, i) => i !== idx);
    setChildren(next.length ? next : ['']);
  };

  const updateChild = (idx: number, value: string) => {
    setChildren((prev) => prev.map((c, i) => (i === idx ? value : c)));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!parentName.trim()) {
      toast.error('Parent module name is required.');
      return;
    }
    if (!Number.isFinite(moduleId)) {
      toast.error('Invalid module id.');
      return;
    }
    const filtered = children
      .map((c) => c.trim())
      .filter(Boolean)
      .map((moduleName) => ({ moduleName }));

    update.mutate(
      {
        mid: moduleId,
        mname: parentName.trim(),
        childModule: JSON.stringify(filtered),
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Module updated');
          navigate('/module');
        },
        onError: () => toast.error('Could not update the module.'),
      },
    );
  };

  if (detail.isLoading) {
    return (
      <ScrollPage>
        <PageHeader title="Edit Module" description="Loading…" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-9 w-full max-w-md" />
            <Skeleton className="mt-4 h-9 w-full max-w-md" />
            <Skeleton className="mt-2 h-9 w-full max-w-md" />
          </CardContent>
        </Card>
      </ScrollPage>
    );
  }

  return (
    <ScrollPage>
      <PageHeader
        title="Edit Module"
        description="Update the permission catalog entry. Existing ACL settings keep their saved permissions."
        actions={
          <Button asChild variant="outline">
            <Link to="/module">
              <ArrowLeft className="h-4 w-4" />
              Back to list
            </Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Module details</CardTitle>
          <CardDescription>
            Rename the parent and add or remove child actions. Removed children
            are dropped on save.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="grid gap-5">
            <div className="grid gap-1.5 sm:max-w-md">
              <Label htmlFor="parentName">
                Parent module<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <Input
                id="parentName"
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
                placeholder="e.g. Manage Campaigns"
              />
            </div>

            <div className="grid gap-2 sm:max-w-md">
              <Label>Child modules</Label>
              <div className="grid gap-2">
                {children.map((value, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={value}
                      onChange={(e) => updateChild(idx, e.target.value)}
                      placeholder="Child module name"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeChild(idx)}
                      aria-label="Remove child"
                      className="h-9 w-9 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addChild}
                >
                  <Plus className="h-4 w-4" />
                  Add child
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button type="submit" disabled={update.isPending}>
                {update.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Save changes
              </Button>
              <Button asChild type="button" variant="ghost">
                <Link to="/module">Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </ScrollPage>
  );
}
